import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArchiveBoxIcon,
  ChevronUpIcon,
  ClipboardDocumentListIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import type { SidebarGroup } from '@/features/groupRail/GroupRailListItem';
import type { SignupSheet } from '@flock-types/signupSheets.types';
import { useSignupSheets, useArchivedSheets } from '@/hooks/useSignupSheets';
import { useGroupLeadership } from '@/hooks/useGroupLeadership';
import SheetCard from './SheetCard';
import SheetCreateModal from './SheetCreateModal';

interface Props {
  group: SidebarGroup;
}

/** Sign-up sheet list for a group: active sheets + leader archived toggle. */
export default function SheetListPane({ group }: Props) {
  const navigate = useNavigate();
  const { sheets, loading, error, refetch, createSheet } = useSignupSheets(group.id, group.orgId);
  const isLeader = useGroupLeadership(group.id);
  const [showArchived, setShowArchived] = useState(false);
  const [creating, setCreating] = useState(false);
  const archived = useArchivedSheets(group.id, showArchived, group.orgId);

  const openSheet = (sheet: SignupSheet) => navigate(`/signups/${group.id}/s/${sheet.id}`);

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between border-b border-flock-gray-200 bg-white px-6 py-4">
        <div className="min-w-0">
          <h1 className="truncate text-lg font-semibold text-flock-navy-900">Sign-Up Sheets</h1>
          <p className="truncate text-sm text-flock-navy-500">
            {group.name} · {group.orgName}
          </p>
        </div>
        {isLeader && (
          <button
            onClick={() => setCreating(true)}
            className="min-h-touch flex shrink-0 items-center gap-1.5 rounded-lg bg-flock-navy-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-flock-navy-700"
          >
            <PlusIcon className="h-4 w-4" />
            New Sheet
          </button>
        )}
      </div>

      <div className="flex-1 overflow-y-auto p-6">
        {loading ? (
          <div className="flex h-full items-center justify-center text-sm text-flock-navy-400">
            Loading sign-up sheets…
          </div>
        ) : error ? (
          <div className="flex h-full flex-col items-center justify-center gap-3">
            <p className="text-sm text-flock-navy-500">{error}</p>
            <button
              onClick={() => refetch()}
              className="rounded-lg bg-flock-navy-600 px-4 py-2 text-sm font-medium text-white hover:bg-flock-navy-700"
            >
              Try Again
            </button>
          </div>
        ) : sheets.length === 0 && !showArchived ? (
          <div className="flex h-full flex-col items-center justify-center gap-2 text-center">
            <ClipboardDocumentListIcon className="h-12 w-12 text-flock-navy-300" />
            <h2 className="text-lg font-semibold text-flock-navy-700">No Sign-Up Sheets</h2>
            <p className="max-w-sm text-sm text-flock-navy-500">
              {isLeader
                ? 'Create a sign-up sheet for your group to coordinate meals, volunteers, and more.'
                : "Your group leader hasn't created any sign-up sheets yet."}
            </p>
            {isLeader && (
              <button
                onClick={() => setCreating(true)}
                className="mt-4 rounded-lg bg-flock-navy-600 px-6 py-3 text-sm font-semibold text-white hover:bg-flock-navy-700"
              >
                Create Sign-Up Sheet
              </button>
            )}
            {isLeader && (
              <button
                onClick={() => setShowArchived(true)}
                className="mt-2 flex items-center gap-1.5 py-2 text-sm font-medium text-flock-navy-500 hover:text-flock-navy-700"
              >
                <ArchiveBoxIcon className="h-4 w-4" />
                View Archived
              </button>
            )}
          </div>
        ) : (
          <div className="mx-auto max-w-3xl space-y-3">
            {sheets.map((sheet) => (
              <SheetCard key={sheet.id} sheet={sheet} onOpen={openSheet} />
            ))}

            {isLeader && (
              <button
                onClick={() => setShowArchived((v) => !v)}
                className="mx-auto flex items-center gap-1.5 py-2 text-sm font-medium text-flock-navy-500 hover:text-flock-navy-700"
              >
                {showArchived ? (
                  <ChevronUpIcon className="h-4 w-4" />
                ) : (
                  <ArchiveBoxIcon className="h-4 w-4" />
                )}
                {showArchived ? 'Hide Archived' : 'View Archived'}
              </button>
            )}
            {showArchived &&
              (archived.loading ? (
                <p className="py-4 text-center text-sm text-flock-navy-400">Loading archived…</p>
              ) : archived.sheets.length === 0 ? (
                <p className="py-4 text-center text-sm text-flock-navy-400">No archived sheets</p>
              ) : (
                <>
                  <div className="px-1 text-xs font-semibold uppercase tracking-wide text-flock-navy-400">
                    Archived
                  </div>
                  {archived.sheets.map((sheet) => (
                    <div key={sheet.id} className="opacity-60">
                      <SheetCard sheet={sheet} onOpen={openSheet} />
                    </div>
                  ))}
                </>
              ))}
          </div>
        )}
      </div>

      {creating && (
        <SheetCreateModal
          createSheet={createSheet}
          onCreated={(sheetId) => {
            setCreating(false);
            navigate(`/signups/${group.id}/s/${sheetId}`);
          }}
          onClose={() => setCreating(false)}
        />
      )}
    </div>
  );
}
